import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Lock, Share2 } from "lucide-react";
import { sfx, setMuted, isMuted, unlockAudio } from "@/game/audio";
import {
  SKINS,
  getSkin,
  getUnlockedSkins,
  loadSelectedSkin,
  saveSelectedSkin,
  detectNewlyUnlocked,
} from "@/game/skins";
import { shareRekt } from "@/game/shareCard";
import { Confetti } from "@/game/Confetti";

/* ---------- Game constants (virtual world coords) ---------- */
const W = 480;
const H = 800;
const GROUND_H = 90;
const GRAVITY = 0.42;
const FLAP = -7.6;
const MAX_FALL = 11;
const PIPE_W = 72;
const PIPE_GAP_MIN = 150;
const PIPE_GAP_MAX = 210;
const PIPE_SPACING = 240; // horizontal distance between pipe pairs
const BASE_SPEED = 2.4;
const SPEED_RAMP = 0.00018; // per ms

const BG_MEMES = ["TO THE MOON", "WAGMI", "HODL", "GM", "NGMI? NAH", "100x"];

/* ---------- Helpers ---------- */
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);
const choice = (arr) => arr[(Math.random() * arr.length) | 0];

/* ---------- Component ---------- */
export default function FlappyPepe() {
  const canvasRef = useRef(null);
  const stateRef = useRef(createInitialState());
  const [screen, setScreen] = useState("start"); // 'start' | 'playing' | 'gameover'
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => {
    const v = parseInt(localStorage.getItem("flappy-pepe-best") || "0", 10);
    return Number.isFinite(v) ? v : 0;
  });
  const [muteFlag, setMuteFlag] = useState(true);
  const [activeSkin, setActiveSkin] = useState(() => loadSelectedSkin(0));
  const [justUnlocked, setJustUnlocked] = useState([]);
  const [sharing, setSharing] = useState(false);

  // Reload selected skin against stored best on mount (handles bootstrap order)
  useEffect(() => {
    setActiveSkin(loadSelectedSkin(best));
    stateRef.current.skinId = loadSelectedSkin(best);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep draw loop in sync with active skin
  useEffect(() => {
    stateRef.current.skinId = activeSkin;
  }, [activeSkin]);

  // Fanfare jingle when a new skin unlocks
  useEffect(() => {
    if (screen === "gameover" && justUnlocked.length) sfx.fanfare();
  }, [screen, justUnlocked]);

  /* ---------- resize canvas to container with DPR ---------- */
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const resize = () => {
      const parent = cvs.parentElement;
      if (!parent) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = parent.clientWidth;
      const ch = parent.clientHeight;
      cvs.width = Math.round(cw * dpr);
      cvs.height = Math.round(ch * dpr);
      cvs.style.width = cw + "px";
      cvs.style.height = ch + "px";
      stateRef.current.dpr = dpr;
      stateRef.current.cw = cw;
      stateRef.current.ch = ch;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  /* ---------- main loop ---------- */
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    let raf;
    let last = performance.now();

    const loop = (t) => {
      const dt = Math.min(40, t - last);
      last = t;
      step(stateRef.current, dt, screen);
      draw(ctx, cvs, stateRef.current, screen);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [screen]);

  /* ---------- game logic step (called from loop via closure) ---------- */
  function step(s, dt, scr) {
    s.t += dt;
    // Parallax always moves
    s.cityX = (s.cityX - 0.2 * dt) % W;
    s.cloudX = (s.cloudX - 0.05 * dt) % W;
    s.memeX = (s.memeX - 0.15 * dt) % (W * 2);
    s.groundX = (s.groundX - (scr === "playing" ? s.speed : 1.4) * (dt / 16)) % 40;

    // Pepe idle bob when not playing
    if (scr !== "playing") {
      s.pepe.y = H / 2 + Math.sin(s.t / 350) * 14;
      s.pepe.vy = 0;
      s.pepe.rot = Math.sin(s.t / 350) * 0.08;
    }

    if (scr === "playing") {
      // Difficulty ramp
      s.speed = BASE_SPEED + s.t * SPEED_RAMP;

      // Physics
      s.pepe.vy = clamp(s.pepe.vy + GRAVITY * (dt / 16), -14, MAX_FALL);
      s.pepe.y += s.pepe.vy * (dt / 16);
      s.pepe.rot = clamp(s.pepe.vy / 10, -0.5, 1.1);
      s.flapAnim = Math.max(0, s.flapAnim - dt);

      // Move pipes
      for (const p of s.pipes) p.x -= s.speed * (dt / 16);

      // Spawn new pipe pair
      const lastP = s.pipes[s.pipes.length - 1];
      if (!lastP || lastP.x < W - PIPE_SPACING) {
        const gap = rand(PIPE_GAP_MIN, PIPE_GAP_MAX);
        const topMin = 70;
        const topMax = H - GROUND_H - gap - 70;
        const topH = rand(topMin, topMax);
        s.pipes.push({
          x: W + 20,
          topH,
          gap,
          variant: choice(["green", "red", "coin", "rocket"]),
          scored: false,
        });
      }

      // Remove off-screen
      s.pipes = s.pipes.filter((p) => p.x + PIPE_W > -40);

      // Score + collisions
      const px = W * 0.28;
      const py = s.pepe.y;
      const pr = 22;
      for (const p of s.pipes) {
        // scoring
        if (!p.scored && p.x + PIPE_W < px - pr) {
          p.scored = true;
          s.score += 1;
          setScore(s.score);
          sfx.point();
        }
        // collision
        if (p.x < px + pr && p.x + PIPE_W > px - pr) {
          if (py - pr < p.topH || py + pr > p.topH + p.gap) {
            triggerGameOver(s);
          }
        }
      }
      // ground / ceiling
      if (s.pepe.y + pr > H - GROUND_H || s.pepe.y - pr < 0) {
        triggerGameOver(s);
      }
    }

    // Particles (always update if any)
    for (const part of s.particles) {
      part.x += part.vx * (dt / 16);
      part.y += part.vy * (dt / 16);
      part.vy += 0.35 * (dt / 16);
      part.life -= dt;
      part.rot += part.vr * (dt / 16);
    }
    s.particles = s.particles.filter((p) => p.life > 0);
  }

  function triggerGameOver(s) {
    if (s.dead) return;
    s.dead = true;
    sfx.crash();
    // Burst of coins/stars/slime
    for (let i = 0; i < 26; i++) {
      s.particles.push({
        x: W * 0.28,
        y: s.pepe.y,
        vx: rand(-6, 6),
        vy: rand(-9, -2),
        rot: rand(0, Math.PI * 2),
        vr: rand(-0.4, 0.4),
        life: rand(700, 1300),
        kind: choice(["coin", "star", "slime"]),
        size: rand(10, 18),
      });
    }
    // delay transition slightly to let crash sfx + particles register
    setTimeout(() => {
      const final = s.score;
      setScore(final);
      setBest((b) => {
        const nb = Math.max(b, final);
        if (nb > b) {
          localStorage.setItem("flappy-pepe-best", String(nb));
          const unlocked = detectNewlyUnlocked(b, nb);
          if (unlocked.length) setJustUnlocked(unlocked);
        }
        return nb;
      });
      setScreen("gameover");
    }, 450);
  }

  /* ---------- flap handler ---------- */
  const flap = useCallback(() => {
    unlockAudio();
    const s = stateRef.current;
    if (screen === "start") {
      startRun();
      return;
    }
    if (screen === "gameover") {
      // ignore; use Run It Back button or space handled separately
      return;
    }
    s.pepe.vy = FLAP;
    s.flapAnim = 140;
    sfx.flap();
  }, [screen]);

  const startRun = useCallback(() => {
    unlockAudio();
    const s = stateRef.current;
    s.pepe.y = H * 0.42;
    s.pepe.vy = FLAP * 0.7;
    s.pepe.rot = -0.3;
    s.pipes = [];
    s.particles = [];
    s.score = 0;
    s.speed = BASE_SPEED;
    s.dead = false;
    s.t = 0;
    s.flapAnim = 140;
    s.skinId = activeSkin;
    setScore(0);
    setJustUnlocked([]);
    sfx.stopFanfare();
    sfx.start();
    setScreen("playing");
  }, [activeSkin]);

  /* ---------- input: pointer, keyboard ---------- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "ArrowUp" || e.key === " ") {
        e.preventDefault();
        if (screen === "start") startRun();
        else if (screen === "playing") flap();
        else if (screen === "gameover") startRun();
      }
      if (e.key === "m" || e.key === "M") {
        toggleMute();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [screen, flap, startRun]);

  const onCanvasPointer = (e) => {
    e.preventDefault();
    if (screen === "start") startRun();
    else if (screen === "playing") flap();
  };

  const toggleMute = () => {
    const next = !isMuted();
    setMuted(next);
    setMuteFlag(next);
  };

  return (
    <div className="fp-stage" data-testid="fp-stage">
      <div className="fp-cabinet" data-testid="fp-cabinet">
        <canvas
          ref={canvasRef}
          className="fp-canvas"
          data-testid="fp-canvas"
          onPointerDown={onCanvasPointer}
        />
        <div className="fp-scanlines" aria-hidden="true" />

        {/* HUD */}
        {screen === "playing" && (
          <div className="fp-hud">
            <div className="fp-score" data-testid="fp-score">
              {score}
            </div>
          </div>
        )}

        {/* Mute button */}
        <button
          type="button"
          className="fp-mute"
          data-testid="mute-toggle"
          data-muted={muteFlag ? "true" : "false"}
          aria-label={muteFlag ? "Unmute sound" : "Mute sound"}
          onClick={toggleMute}
        >
          {muteFlag ? <VolumeX size={22} /> : <Volume2 size={22} />}
        </button>

        {/* START SCREEN */}
        {screen === "start" && (
          <div className="fp-overlay" data-testid="start-screen">
            <h1 className="fp-title">FLAPPY<br />PEPE</h1>
            <div className="fp-sub">CLICK · TAP · SPACE TO FLAP</div>
            <div className="fp-best" data-testid="start-best">
              BEST: {best}
            </div>

            <SkinPicker
              best={best}
              activeSkin={activeSkin}
              onSelect={(id) => {
                setActiveSkin(id);
                saveSelectedSkin(id);
                sfx.point();
              }}
            />

            <button
              type="button"
              className="fp-btn"
              data-testid="play-button"
              onClick={startRun}
            >
              ▶ PLAY
            </button>
            <div
              style={{
                fontFamily: '"Press Start 2P", monospace',
                fontSize: 9,
                color: "#9945ff",
                textShadow: "2px 2px 0 #000",
                letterSpacing: 2,
                marginTop: 6,
              }}
            >
              DEGEN EDITION · v1.1
            </div>
          </div>
        )}

        {/* GAME OVER */}
        {screen === "gameover" && justUnlocked.length > 0 && <Confetti />}
        {screen === "gameover" && (
          <div className="fp-overlay" data-testid="gameover-screen">
            <div className="fp-rekt">REKT 💀</div>
            <div className="fp-skill">SKILL ISSUE</div>

            {justUnlocked.length > 0 && (
              <div className="fp-unlock" data-testid="skin-unlocked">
                NEW SKIN UNLOCKED:{" "}
                <span style={{ color: "#39ff14" }}>
                  {justUnlocked
                    .map((id) => getSkin(id).name.toUpperCase())
                    .join(", ")}
                </span>
              </div>
            )}

            <div className="fp-score-box">
              <div>
                <div className="label">RUN</div>
                <div className="val" data-testid="run-score">
                  {score}
                </div>
              </div>
              <div>
                <div className="label">BEST</div>
                <div className="val" data-testid="best-score">
                  {best}
                </div>
              </div>
            </div>

            <SkinPicker
              compact
              best={best}
              activeSkin={activeSkin}
              onSelect={(id) => {
                setActiveSkin(id);
                saveSelectedSkin(id);
                sfx.point();
              }}
            />

            <div className="fp-btn-row">
              <button
                type="button"
                className="fp-btn"
                data-testid="run-it-back"
                onClick={startRun}
              >
                ↻ RUN IT BACK
              </button>
              <button
                type="button"
                className="fp-btn secondary"
                data-testid="share-rekt"
                disabled={sharing}
                onClick={async () => {
                  if (sharing) return;
                  setSharing(true);
                  try {
                    await shareRekt({
                      score,
                      best,
                      drawCharacter: (c) =>
                        drawPepe(c, { x: 0, y: 0, rot: 0 }, 0, true, activeSkin),
                    });
                    sfx.point();
                  } finally {
                    setSharing(false);
                  }
                }}
              >
                <Share2 size={18} /> {sharing ? "..." : "SHARE REKT"}
              </button>
            </div>

            <div
              style={{
                fontFamily: '"Press Start 2P", monospace',
                fontSize: 9,
                color: "#facc15",
                textShadow: "2px 2px 0 #000",
                letterSpacing: 2,
              }}
            >
              SPACE TO RE-APE
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SkinPicker({ best, activeSkin, onSelect, compact = false }) {
  const unlocked = getUnlockedSkins(best);
  return (
    <div className={`fp-skins ${compact ? "compact" : ""}`} data-testid="skin-picker">
      <div className="fp-skins-label">{compact ? "SWAP PEPE" : "PICK YOUR PEPE"}</div>
      <div className="fp-skins-row">
        {SKINS.map((skin) => (
          <SkinTile
            key={skin.id}
            skin={skin}
            locked={!unlocked.includes(skin.id)}
            active={activeSkin === skin.id}
            onSelect={onSelect}
          />
        ))}
      </div>
      <div className="fp-skins-name" data-testid="active-skin-name">
        {getSkin(activeSkin).name.toUpperCase()}
      </div>
    </div>
  );
}

function SkinTile({ skin, locked, active, onSelect }) {
  const ref = useRef(null);
  useEffect(() => {
    const cvs = ref.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    ctx.clearRect(0, 0, cvs.width, cvs.height);
    ctx.save();
    ctx.translate(cvs.width / 2, cvs.height / 2 + 4);
    ctx.scale(0.95, 0.95);
    drawPepe(ctx, { x: 0, y: 0, rot: 0 }, 0, false, skin.id);
    ctx.restore();
  }, [skin.id]);
  return (
    <button
      type="button"
      className={`fp-skin ${active ? "active" : ""} ${locked ? "locked" : ""}`}
      data-testid={`skin-${skin.id}`}
      aria-label={locked ? `${skin.name} locked, reach ${skin.unlockBest}` : skin.name}
      disabled={locked}
      onClick={() => onSelect(skin.id)}
    >
      <canvas ref={ref} width={84} height={84} />
      {locked && (
        <span className="fp-skin-lock">
          <Lock size={14} /> {skin.unlockBest}
        </span>
      )}
      {!locked && <span className="fp-skin-tag">{skin.tag}</span>}
    </button>
  );
}


/* ---------- Initial state ---------- */
function createInitialState() {
  return {
    t: 0,
    cw: W,
    ch: H,
    dpr: 1,
    speed: BASE_SPEED,
    pepe: { x: W * 0.28, y: H / 2, vy: 0, rot: 0 },
    pipes: [],
    particles: [],
    score: 0,
    dead: false,
    flapAnim: 0,
    cityX: 0,
    cloudX: 0,
    memeX: 0,
    groundX: 0,
    // precomputed background decoration anchors
    clouds: Array.from({ length: 5 }, () => ({
      x: Math.random() * W,
      y: 60 + Math.random() * 180,
      r: 24 + Math.random() * 22,
    })),
    stars: Array.from({ length: 40 }, () => ({
      x: Math.random() * W,
      y: Math.random() * (H - GROUND_H - 100),
      r: Math.random() * 1.6 + 0.4,
      tw: Math.random() * Math.PI * 2,
    })),
    memes: Array.from({ length: 6 }, () => ({
      x: Math.random() * W * 2,
      y: 120 + Math.random() * (H - GROUND_H - 300),
      text: choice(BG_MEMES),
      hue: Math.random() * 360,
    })),
    rockets: Array.from({ length: 2 }, () => ({
      x: Math.random() * W,
      y: 100 + Math.random() * (H - GROUND_H - 260),
      vx: 0.4 + Math.random() * 0.5,
    })),
  };
}

/* ---------- Rendering ---------- */
function draw(ctx, cvs, s, scr) {
  const { dpr } = s;
  const cw = cvs.width;
  const ch = cvs.height;
  const scale = Math.min(cw / W, ch / H);
  const offX = (cw - W * scale) / 2;
  const offY = (ch - H * scale) / 2;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, cw, ch);

  // Sky gradient
  const sky = ctx.createLinearGradient(0, 0, 0, ch);
  sky.addColorStop(0, "#1a0b2e");
  sky.addColorStop(0.55, "#431478");
  sky.addColorStop(1, "#9945ff");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, cw, ch);

  // Scale world
  ctx.translate(offX, offY);
  ctx.scale(scale, scale);

  // Stars
  for (const st of s.stars) {
    const a = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(s.t / 400 + st.tw));
    ctx.fillStyle = `rgba(255,255,255,${a})`;
    ctx.beginPath();
    ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Moon
  ctx.fillStyle = "#facc15";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(W - 80, 110, 36, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#eab308";
  ctx.beginPath();
  ctx.arc(W - 70, 100, 6, 0, Math.PI * 2);
  ctx.arc(W - 92, 118, 4, 0, Math.PI * 2);
  ctx.arc(W - 76, 130, 5, 0, Math.PI * 2);
  ctx.fill();

  // Background memes layer (slow parallax)
  ctx.font = 'bold 22px "Bangers", Impact, sans-serif';
  ctx.textBaseline = "middle";
  for (const m of s.memes) {
    const x = ((m.x + s.memeX) % (W * 2) + W * 2) % (W * 2) - W * 0.5;
    ctx.save();
    ctx.translate(x, m.y);
    ctx.rotate(-0.08);
    ctx.fillStyle = `hsla(${m.hue}, 100%, 70%, 0.35)`;
    ctx.strokeStyle = "rgba(0,0,0,0.5)";
    ctx.lineWidth = 3;
    ctx.strokeText(m.text, 0, 0);
    ctx.fillText(m.text, 0, 0);
    ctx.restore();
  }

  // Background rockets
  for (const r of s.rockets) {
    r.x += r.vx * 0.6;
    if (r.x > W + 40) {
      r.x = -40;
      r.y = 100 + Math.random() * (H - GROUND_H - 260);
    }
    ctx.font = "28px serif";
    ctx.fillText("🚀", r.x, r.y);
  }

  // Clouds
  for (const c of s.clouds) {
    const x = ((c.x + s.cloudX) % W + W) % W;
    drawCloud(ctx, x, c.y, c.r);
  }

  // City silhouette (parallax)
  drawCity(ctx, s.cityX);

  // Pipes (crypto candles)
  for (const p of s.pipes) drawCandle(ctx, p);

  // Pepe
  drawPepe(ctx, s.pepe, s.flapAnim, s.dead, s.skinId || "classic");

  // Particles
  for (const part of s.particles) drawParticle(ctx, part);

  // Ground
  drawGround(ctx, s.groundX);

  ctx.restore();
}

/* ---------- Draw helpers (virtual coords) ---------- */
function drawCloud(ctx, x, y, r) {
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.arc(x + r * 0.9, y + 4, r * 0.75, 0, Math.PI * 2);
  ctx.arc(x - r * 0.8, y + 6, r * 0.65, 0, Math.PI * 2);
  ctx.arc(x + r * 0.3, y - r * 0.5, r * 0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
}

function drawCity(ctx, offX) {
  const baseY = H - GROUND_H;
  ctx.fillStyle = "#1b0330";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  const buildings = [
    { w: 60, h: 140 },
    { w: 40, h: 90 },
    { w: 70, h: 170 },
    { w: 50, h: 110 },
    { w: 80, h: 190 },
    { w: 44, h: 100 },
    { w: 64, h: 150 },
    { w: 36, h: 80 },
  ];
  let x = offX;
  for (let repeat = 0; repeat < 3; repeat++) {
    for (const b of buildings) {
      ctx.fillRect(x, baseY - b.h, b.w, b.h);
      ctx.strokeRect(x, baseY - b.h, b.w, b.h);
      // tiny windows
      ctx.fillStyle = "#facc15";
      for (let wy = baseY - b.h + 10; wy < baseY - 12; wy += 16) {
        for (let wx = x + 6; wx < x + b.w - 8; wx += 14) {
          if ((wx * wy) % 7 < 4) ctx.fillRect(wx, wy, 5, 7);
        }
      }
      ctx.fillStyle = "#1b0330";
      x += b.w;
    }
  }
}

function drawCandle(ctx, p) {
  const { x, topH, gap, variant } = p;
  const isGreen = variant === "green" || variant === "coin";
  const body = isGreen ? "#39ff14" : "#ff2d2d";
  const wick = "#09090b";

  // TOP candle (hangs from ceiling, "wick" extends down into the gap)
  // Body
  ctx.fillStyle = body;
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 5;
  const bodyPad = 8;
  // top body
  ctx.fillRect(x + bodyPad, 0, PIPE_W - bodyPad * 2, topH - 8);
  ctx.strokeRect(x + bodyPad, 0, PIPE_W - bodyPad * 2, topH - 8);
  // top wick (sticking into gap)
  ctx.fillStyle = wick;
  ctx.fillRect(x + PIPE_W / 2 - 3, topH - 14, 6, 14);

  // Bottom candle
  const by = topH + gap;
  const bh = H - GROUND_H - by;
  // bottom wick (sticking up into gap)
  ctx.fillStyle = wick;
  ctx.fillRect(x + PIPE_W / 2 - 3, by, 6, 14);
  // bottom body
  ctx.fillStyle = body;
  ctx.fillRect(x + bodyPad, by + 8, PIPE_W - bodyPad * 2, bh - 8);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 5;
  ctx.strokeRect(x + bodyPad, by + 8, PIPE_W - bodyPad * 2, bh - 8);

  // Variant decoration on the TOP candle lip
  if (variant === "coin") {
    drawCoin(ctx, x + PIPE_W / 2, 24);
  } else if (variant === "rocket") {
    ctx.font = "32px serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText("🚀", x + PIPE_W / 2, 36);
    ctx.textAlign = "start";
  }

  // Little arrow on bottom candle lip
  ctx.fillStyle = "#fff";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.beginPath();
  if (isGreen) {
    ctx.moveTo(x + PIPE_W / 2, by + 18);
    ctx.lineTo(x + PIPE_W / 2 - 8, by + 30);
    ctx.lineTo(x + PIPE_W / 2 + 8, by + 30);
  } else {
    ctx.moveTo(x + PIPE_W / 2, by + 30);
    ctx.lineTo(x + PIPE_W / 2 - 8, by + 18);
    ctx.lineTo(x + PIPE_W / 2 + 8, by + 18);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function drawCoin(ctx, x, y) {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = "#facc15";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(0, 0, 14, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#000";
  ctx.font = 'bold 16px "Bangers", Impact, sans-serif';
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("$", 0, 1);
  ctx.textAlign = "start";
  ctx.restore();
}

function drawGround(ctx, offX) {
  const y = H - GROUND_H;
  // base
  ctx.fillStyle = "#5a1aa3";
  ctx.fillRect(0, y, W, GROUND_H);
  // grass stripe
  ctx.fillStyle = "#39ff14";
  ctx.fillRect(0, y, W, 10);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(W, y);
  ctx.stroke();
  // repeating stripes
  for (let i = -40; i < W + 40; i += 40) {
    const sx = i + offX;
    ctx.fillStyle = "#431478";
    ctx.beginPath();
    ctx.moveTo(sx, y + 18);
    ctx.lineTo(sx + 20, y + 18);
    ctx.lineTo(sx + 10, y + 38);
    ctx.closePath();
    ctx.fill();
  }
  // candy stripe
  for (let i = -40; i < W + 40; i += 24) {
    const sx = i + offX * 1.5;
    ctx.fillStyle = (i / 24) % 2 === 0 ? "#facc15" : "#ff2d2d";
    ctx.fillRect(sx, y + GROUND_H - 14, 12, 14);
  }
}

/* ---------- PEPE ---------- */
function drawPepe(ctx, pepe, flapAnim, dead, skinId) {
  const skin = getSkin(skinId);
  const x = pepe.x;
  const y = pepe.y;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(pepe.rot);

  // Shadow blob below (fixed below character)
  ctx.save();
  ctx.rotate(-pepe.rot);
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(0, 28, 20, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Rare rainbow glow aura
  if (skin.accessories?.includes("rainbow")) {
    const g = ctx.createRadialGradient(0, 0, 10, 0, 0, 42);
    g.addColorStop(0, "rgba(255,255,255,0)");
    g.addColorStop(0.6, "rgba(255,0,180,0.35)");
    g.addColorStop(1, "rgba(0,220,255,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, 42, 0, Math.PI * 2);
    ctx.fill();
  }

  // Body (frog head)
  ctx.fillStyle = skin.body;
  ctx.strokeStyle = "#09090b";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(0, 2, 26, 22, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Cheek highlight
  ctx.fillStyle = skin.cheek;
  ctx.beginPath();
  ctx.ellipse(-8, 8, 10, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  // Chad beard
  if (skin.accessories?.includes("chinbeard")) {
    ctx.fillStyle = "#2a1a0a";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-10, 18);
    ctx.quadraticCurveTo(0, 30, 10, 18);
    ctx.quadraticCurveTo(6, 24, 0, 24);
    ctx.quadraticCurveTo(-6, 24, -10, 18);
    ctx.fill();
    ctx.stroke();
  }

  // Eye bumps (classic Pepe-style on top of head)
  const eyeY = -16;
  const eyeR = 11;
  for (const ex of [-11, 11]) {
    ctx.fillStyle = skin.body;
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(ex, eyeY, eyeR, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // white
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(ex, eyeY, 7, 0, Math.PI * 2);
    ctx.fill();
    // pupil (dizzy when dead)
    ctx.fillStyle = "#09090b";
    if (dead) {
      // X eyes
      ctx.strokeStyle = "#09090b";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(ex - 5, eyeY - 5);
      ctx.lineTo(ex + 5, eyeY + 5);
      ctx.moveTo(ex + 5, eyeY - 5);
      ctx.lineTo(ex - 5, eyeY + 5);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(ex + 2, eyeY + 1, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Chad sunglasses — black bar across both eye bumps
  if (skin.accessories?.includes("shades")) {
    ctx.save();
    ctx.fillStyle = "#09090b";
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 3;
    // Left lens
    ctx.beginPath();
    ctx.roundRect
      ? ctx.roundRect(-22, -22, 20, 12, 3)
      : ctx.rect(-22, -22, 20, 12);
    ctx.fill();
    // Right lens
    ctx.beginPath();
    ctx.roundRect
      ? ctx.roundRect(2, -22, 20, 12, 3)
      : ctx.rect(2, -22, 20, 12);
    ctx.fill();
    // Bridge
    ctx.fillRect(-2, -18, 4, 3);
    // Shine
    ctx.fillStyle = "#39ff14";
    ctx.fillRect(-18, -20, 4, 3);
    ctx.fillRect(6, -20, 4, 3);
    ctx.restore();
  }

  // Mouth (classic smug Pepe)
  ctx.strokeStyle = "#09090b";
  ctx.lineWidth = 4;
  ctx.beginPath();
  if (dead) {
    ctx.moveTo(-12, 12);
    ctx.quadraticCurveTo(0, 22, 12, 12);
  } else {
    ctx.moveTo(-14, 8);
    ctx.quadraticCurveTo(-6, 16, 0, 14);
    ctx.quadraticCurveTo(6, 12, 14, 8);
  }
  ctx.stroke();

  // Lips smirk
  if (!dead) {
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(-10, 14);
    ctx.lineTo(10, 14);
    ctx.stroke();
  }

  // Arms (flap based on flapAnim timer)
  const flapT = flapAnim / 140;
  const armAngle = -0.6 - flapT * 1.2;
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(side * 20, 6);
    ctx.rotate(side * armAngle);
    ctx.fillStyle = skin.body;
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(side * 6, 2, 10, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // Astronaut helmet — big transparent bubble over head
  if (skin.accessories?.includes("helmet")) {
    ctx.save();
    // Helmet glass
    ctx.fillStyle = "rgba(190, 240, 255, 0.28)";
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, -6, 32, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Gold visor reflection
    ctx.fillStyle = "rgba(250, 204, 21, 0.55)";
    ctx.beginPath();
    ctx.ellipse(-10, -14, 10, 5, -0.4, 0, Math.PI * 2);
    ctx.fill();
    // Shoulder ring
    ctx.strokeStyle = "#facc15";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, -6, 32, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // Rare crown
  if (skin.accessories?.includes("crown")) {
    ctx.save();
    ctx.translate(0, -34);
    ctx.fillStyle = "#facc15";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(-14, 6);
    ctx.lineTo(-14, -4);
    ctx.lineTo(-7, 2);
    ctx.lineTo(0, -8);
    ctx.lineTo(7, 2);
    ctx.lineTo(14, -4);
    ctx.lineTo(14, 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#ff2d2d";
    ctx.beginPath();
    ctx.arc(0, 2, 2.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Little tongue/sweat if dead
  if (dead) {
    ctx.fillStyle = "#67e8f9";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(16, -4, 3, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();
}

function drawParticle(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  const alpha = clamp(p.life / 900, 0, 1);
  ctx.globalAlpha = alpha;
  if (p.kind === "coin") {
    ctx.fillStyle = "#facc15";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, p.size * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#000";
    ctx.font = 'bold 12px "Bangers", Impact, sans-serif';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("$", 0, 1);
  } else if (p.kind === "slime") {
    ctx.fillStyle = "#39ff14";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(0, 0, p.size * 0.8, p.size * 0.55, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else {
    // star
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    const r = p.size * 0.6;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
      const b = a + Math.PI / 5;
      ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      ctx.lineTo(Math.cos(b) * r * 0.45, Math.sin(b) * r * 0.45);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}
